import React, { useMemo } from 'react';
import { ShieldAlert, Scale, BookOpen, Shield, AlertTriangle, Info } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Cell } from 'recharts';
import { Article, FrameworkLevel, ContentRole, JdaClassification, antisemitismLabels, contentRoleLabels, jdaClassificationLabels } from '../lib/data';
interface Props {
  articles: Article[];
  // Title can be tuned per context (rassegna vs statistiche)
  heading?: string;
}
const LEVEL_ORDER: FrameworkLevel[] = ['none', 'low', 'medium', 'high'];
const LEVEL_COLOR: Record<FrameworkLevel, string> = {
  none: '#4A7C59',
  low: '#E0A100',
  medium: '#E07A00',
  high: '#B85450'
};
const LEVEL_DOT: Record<FrameworkLevel, string> = {
  none: 'bg-emerald-500',
  low: 'bg-amber-500',
  medium: 'bg-orange-500',
  high: 'bg-red-500'
};
function StatCard({
  label,
  value,
  sub,
  accent = false,
  danger = false
}: {
  label: string;
  value: string | number;
  sub?: string;
  accent?: boolean;
  danger?: boolean;
}) {
  return <div className="flex flex-col gap-1.5" data-id="element-45">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-heading" data-id="element-46">
        {label}
      </div>
      <div className={`text-3xl font-light leading-none ${danger ? 'text-red-600' : accent ? 'text-accent' : 'text-foreground'}`} style={{
      fontVariantNumeric: 'tabular-nums'
    }} data-id="element-47">
        {value}
      </div>
      {sub && <div className="text-xs text-muted-foreground" data-id="element-48">{sub}</div>}
    </div>;
}
export function AntisemitismOverview({
  articles,
  heading = 'Antisemitismo rilevato'
}: Props) {
  const data = useMemo(() => {
    const withAnalysis = articles.filter(a => a.antisemitismAnalysis);
    const total = withAnalysis.length;
    const flagged = withAnalysis.filter(a => a.antisemitismAnalysis!.aggregate.level !== 'none');
    const flaggedCount = flagged.length;
    // Aggregate level distribution
    const levelCounts: Record<FrameworkLevel, number> = {
      none: 0,
      low: 0,
      medium: 0,
      high: 0
    };
    withAnalysis.forEach(a => {
      levelCounts[a.antisemitismAnalysis!.aggregate.level] += 1;
    });
    // Average score over flagged
    const avgScore = flaggedCount > 0 ? flagged.reduce((acc, a) => acc + a.antisemitismAnalysis!.aggregate.score, 0) / flaggedCount : 0;
    // Disagreement count
    const disagreementCount = withAnalysis.filter(a => a.antisemitismAnalysis!.aggregate.hasDisagreement).length;
    // Distribution by framework / definition source (how many flagged per framework)
    const frameworkFlags = {
      ihra: withAnalysis.filter(a => a.antisemitismAnalysis!.ihra.level !== 'none').length,
      jda: withAnalysis.filter(a => a.antisemitismAnalysis!.jda.level !== 'none').length,
      deontologico: withAnalysis.filter(a => a.antisemitismAnalysis!.deontologico.level !== 'none').length
    };
    const frameworkData = [{
      key: 'ihra',
      label: 'IHRA',
      value: frameworkFlags.ihra
    }, {
      key: 'jda',
      label: 'JDA',
      value: frameworkFlags.jda
    }, {
      key: 'deontologico',
      label: 'Codice deont.',
      value: frameworkFlags.deontologico
    }];
    // Content role distribution (only for flagged)
    const roleCounts: Record<ContentRole, number> = {
      reported: 0,
      endorsed: 0,
      ambiguous: 0
    };
    flagged.forEach(a => {
      roleCounts[a.antisemitismAnalysis!.aggregate.contentRole] += 1;
    });
    // JDA classification distribution
    const jdaCounts: Record<JdaClassification, number> = {
      antisemitic: 0,
      protected_criticism: 0,
      ambiguous: 0,
      none: 0
    };
    withAnalysis.forEach(a => {
      jdaCounts[a.antisemitismAnalysis!.jda.classification] += 1;
    });
    return {
      total,
      flaggedCount,
      levelCounts,
      avgScore,
      disagreementCount,
      frameworkData,
      roleCounts,
      jdaCounts
    };
  }, [articles]);
  if (data.total === 0) return null;
  const flaggedPct = data.total > 0 ? Math.round(data.flaggedCount / data.total * 100) : 0;
  const severeCount = data.levelCounts.medium + data.levelCounts.high;
  const roleColors: Record<ContentRole, string> = {
    reported: '#0187c3',
    endorsed: '#B85450',
    ambiguous: '#E0A100'
  };
  const jdaColors: Record<JdaClassification, string> = {
    antisemitic: '#B85450',
    protected_criticism: '#4A7C59',
    ambiguous: '#E0A100',
    none: '#64B5F6'
  };
  return <section className="space-y-6" data-id="element-49">
      {/* Header */}
      <div className="flex items-center gap-2" data-id="element-50">
        <ShieldAlert className="h-4 w-4 text-accent" data-id="element-51" />
        <h3 className="text-base font-medium font-heading" data-id="element-52">{heading}</h3>
      </div>

      {/* KPI strip */}
      <div className="bg-card border border-border rounded-lg p-6 grid grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-53">
        <StatCard label="Articoli con rilevazione" value={data.flaggedCount} sub={`${flaggedPct}% di ${data.total} analizzati`} data-id="element-54" />
        <StatCard label="Score medio (segnalati)" value={data.avgScore.toFixed(1)} accent data-id="element-55" />
        <StatCard label="Casi gravi / moderati" value={severeCount} danger={severeCount > 0} data-id="element-56" />
        <StatCard label="Discordanze tra quadri" value={data.disagreementCount} sub="Da verificare" data-id="element-57" />
      </div>

      {/* Charts row: framework distribution + level distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-58">
        {/* Distribution by definition framework */}
        <div className="bg-card border border-border rounded-lg p-6" data-id="element-59">
          <h4 className="text-sm font-medium mb-1" data-id="element-60">
            Rilevazioni per quadro di definizione
          </h4>
          <p className="text-xs text-muted-foreground mb-5" data-id="element-61">
            Quanti articoli risultano segnalati da ciascun quadro (IHRA, JDA,
            Codice deontologico)
          </p>
          <div className="h-56" data-id="element-62">
            <ResponsiveContainer width="100%" height="100%" data-id="element-63">
              <BarChart data={data.frameworkData} margin={{
              top: 5,
              right: 0,
              bottom: 0,
              left: -20
            }} data-id="element-64">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E8E8E5" data-id="element-65" />
                <XAxis dataKey="label" tick={{
                fontSize: 12
              }} stroke="#8B8680" axisLine={false} tickLine={false} dy={8} data-id="element-66" />
                <YAxis tick={{
                fontSize: 12
              }} stroke="#8B8680" axisLine={false} tickLine={false} allowDecimals={false} data-id="element-67" />
                <Tooltip cursor={{
                fill: 'rgba(0,0,0,0.05)'
              }} data-id="element-68" />
                <Bar dataKey="value" fill="#0187c3" radius={[4, 4, 0, 0]} barSize={56} name="Articoli segnalati" data-id="element-69" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Aggregate level distribution */}
        <div className="bg-card border border-border rounded-lg p-6" data-id="element-70">
          <h4 className="text-sm font-medium mb-1" data-id="element-71">
            Distribuzione per livello aggregato
          </h4>
          <p className="text-xs text-muted-foreground mb-5" data-id="element-72">
            Severità complessiva risultante dai tre quadri
          </p>
          <div className="space-y-4 mt-2" data-id="element-73">
            {LEVEL_ORDER.map(lvl => {
            const count = data.levelCounts[lvl];
            const pct = data.total > 0 ? count / data.total * 100 : 0;
            return <div key={lvl} data-id="element-74">
                  <div className="flex items-center justify-between mb-2 text-sm" data-id="element-75">
                    <span className="flex items-center gap-2 text-foreground" data-id="element-76">
                      <span className={`w-2 h-2 rounded-full ${LEVEL_DOT[lvl]}`} data-id="element-77" />
                      {antisemitismLabels[lvl]}
                    </span>
                    <span className="text-muted-foreground" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-78">
                      {count} ({pct.toFixed(0)}%)
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden" data-id="element-79">
                    <div className="h-full rounded-full transition-all" style={{
                  width: `${pct}%`,
                  backgroundColor: LEVEL_COLOR[lvl]
                }} data-id="element-80" />
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>

      {/* Content role + JDA classification */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-81">
        {/* Content role */}
        <div className="bg-card border border-border rounded-lg p-6" data-id="element-82">
          <h4 className="text-sm font-medium mb-1" data-id="element-83">Ruolo del contenuto</h4>
          <p className="text-xs text-muted-foreground mb-5" data-id="element-84">
            Distinzione tra contenuti riportati a fini di cronaca e contenuti
            veicolati dalla testata
          </p>
          <div className="space-y-4" data-id="element-85">
            {(['reported', 'endorsed', 'ambiguous'] as ContentRole[]).map(role => {
            const count = data.roleCounts[role];
            const pct = data.flaggedCount > 0 ? count / data.flaggedCount * 100 : 0;
            return <div key={role} data-id="element-86">
                    <div className="flex items-center justify-between mb-2 text-sm" data-id="element-87">
                      <span className="flex items-center gap-2 text-foreground" data-id="element-88">
                        <span className="w-2 h-2 rounded-full" style={{
                    backgroundColor: roleColors[role]
                  }} data-id="element-89" />
                        {contentRoleLabels[role]}
                      </span>
                      <span className="text-muted-foreground" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-90">
                        {count}
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden" data-id="element-91">
                      <div className="h-full rounded-full transition-all" style={{
                  width: `${pct}%`,
                  backgroundColor: roleColors[role]
                }} data-id="element-92" />
                    </div>
                  </div>;
          })}
          </div>
          <p className="text-[11px] text-muted-foreground mt-4 leading-relaxed" data-id="element-93">
            I contenuti riportati a fini di cronaca non costituiscono condotta
            antisemita della testata.
          </p>
        </div>

        {/* JDA classification */}
        <div className="bg-card border border-border rounded-lg p-6" data-id="element-94">
          <h4 className="text-sm font-medium mb-1" data-id="element-95">Classificazione JDA</h4>
          <p className="text-xs text-muted-foreground mb-5" data-id="element-96">
            Distinzione tra antisemitismo e critica politica legittima
          </p>
          <div className="space-y-4" data-id="element-97">
            {(['antisemitic', 'protected_criticism', 'ambiguous', 'none'] as JdaClassification[]).map(cls => {
            const count = data.jdaCounts[cls];
            const pct = data.total > 0 ? count / data.total * 100 : 0;
            return <div key={cls} data-id="element-98">
                  <div className="flex items-center justify-between mb-2 text-sm" data-id="element-99">
                    <span className="flex items-center gap-2 text-foreground" data-id="element-100">
                      <span className="w-2 h-2 rounded-full" style={{
                    backgroundColor: jdaColors[cls]
                  }} data-id="element-101" />
                      {jdaClassificationLabels[cls]}
                    </span>
                    <span className="text-muted-foreground" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-102">
                      {count}
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden" data-id="element-103">
                    <div className="h-full rounded-full transition-all" style={{
                  width: `${pct}%`,
                  backgroundColor: jdaColors[cls]
                }} data-id="element-104" />
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="flex items-start gap-2.5 px-4 py-3 bg-accent/5 border border-border rounded-lg" data-id="element-105">
        <Info className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" data-id="element-106" />
        <p className="text-xs text-muted-foreground leading-relaxed" data-id="element-107">
          L'analisi confronta ogni articolo con i tre quadri di riferimento
          (IHRA, JDA, Codice deontologico). Le rilevazioni per quadro possono
          sovrapporsi: uno stesso articolo può essere segnalato da più
          definizioni. I dati non sostituiscono la validazione umana.
        </p>
      </div>
    </section>;
}